import { logger } from '../services/logger.ts';
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, CircleDollarSign, HelpCircle, LayoutList, Receipt } from 'lucide-react';
import { useAuth } from '../lib/auth.tsx';
import { PortfolioSummary } from '../components/portfolio/PortfolioSummary.tsx';
import { PortfolioPositions } from '../components/portfolio/PortfolioPositions.tsx';
import { TransactionHistory } from '../components/portfolio/TransactionHistory.tsx';
import { BetStatusExplainer } from '../components/portfolio/BetStatusExplainer.tsx';
import {
  DetailedWalletTransaction,
  LocalPositionWithMarket,
  UserProfile,
  Market as ImportedMarket,
  LMSRMarketState,
} from '../types.ts';
import { useWallet } from '../lib/walletContext.tsx';
import { MarketCard } from '../components/MarketCard.tsx';
import { fetchUserPortfolioData } from '../services/portfolioDataService.ts';

type PortfolioTab = 'overview' | 'positions' | 'transactions' | 'created';

function portfolioTabFromSearchParams(searchParams: URLSearchParams): PortfolioTab {
  const t = searchParams.get('tab');
  if (t === 'positions' || t === 'transactions' || t === 'created' || t === 'overview') {
    return t;
  }
  return 'overview';
}

export function UserPortfolio() {
  const { user } = useAuth();
  const { balance: walletBalance } = useWallet();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const activeTab = portfolioTabFromSearchParams(searchParams);

  const setPortfolioTab = (tab: PortfolioTab) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === 'overview') {
          next.delete('tab');
        } else {
          next.set('tab', tab);
        }
        return next;
      },
      { replace: true },
    );
  };
  
  const [transactions, setTransactions] = useState<DetailedWalletTransaction[]>([]);
  const [positions, setPositions] = useState<LocalPositionWithMarket[]>([]);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [createdMarkets, setCreatedMarkets] = useState<ImportedMarket[]>([]);
  const [lmsrMarkets, setLmsrMarkets] = useState<Record<string, LMSRMarketState> | undefined>(undefined);
  const [settledTrades, setSettledTrades] = useState(0);
  
  // Portfolio Value = wallet cash + current value of open positions.
  // LMSR uses mark-to-market (shares × price); pool uses prior potential-win heuristic.
  const positionsValue = positions
    .filter((pos) => pos.status === 'active')
    .reduce((sum, pos) => {
      if (pos.engine_type === 'lmsr' && pos.current_market_value != null) {
        return sum + pos.current_market_value;
      }
      return sum + (pos.currentPotentialWin ?? 0);
    }, 0);
  const portfolioValue = (walletBalance ?? 0) + positionsValue;

useEffect(() => {
  if (!user) return;

  let isMounted = true;

  const loadPortfolio = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await fetchUserPortfolioData(user.id, { includeWalletBalance: false });

      if (!isMounted) {
        return;
      }

      setPositions(result.positions);
      setTransactions(result.transactions);
      setCreatedMarkets(result.createdMarkets as ImportedMarket[]);
      setLmsrMarkets(result.lmsrMarkets);
      setSettledTrades(result.settledTrades ?? 0);

      const userMetadata = user.user_metadata || {};
      const derivedRole: UserProfile['role'] = (user.app_metadata?.role === 'admin' || user.app_metadata?.role === 'superuser') ? 'admin' : 'user';

      const profile: UserProfile = {
        id: user.id,
        email: user.email || '',
        firstName: userMetadata.first_name || '',
        lastName: userMetadata.last_name || '',
        phone: userMetadata.phone || '',
        avatar_url: userMetadata.avatar_url,
        role: derivedRole,
        created_at: user.created_at || '',
        updated_at: user.updated_at || '',
        total_trades: result.totalTrades,
        win_rate: result.winRate,
        positions: [],
        wallet_balance: walletBalance ?? result.walletBalance ?? 0,
      };

      setUserProfile(profile);
    } catch (err) {
      logger.error('Error fetching portfolio data:', { error: err });
      if (isMounted) {
        setError('Failed to load portfolio data. Please try again.');
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }
  };

  loadPortfolio();

  return () => {
    isMounted = false;
  };
}, [user]);

useEffect(() => {
  if (walletBalance === undefined || walletBalance === null) return;
  setUserProfile(prev => prev ? { ...prev, wallet_balance: walletBalance } : prev);
}, [walletBalance]);
  
  // Calculate aggregate statistics
  const openPositions = positions.filter(pos => pos.status === 'active').length;
  // Use the status of positions to calculate win rate based on completed positions
  const completedPositions = positions.filter(pos => pos.status === 'won' || pos.status === 'lost');
  const winRate = completedPositions.length > 0
    ? (positions.filter(pos => pos.status === 'won').length / completedPositions.length) * 100
    : userProfile?.win_rate || 0; // Fallback to the win_rate from userProfile if no completed positions
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 py-8">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-wayja-teal"></div>
          </div>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 py-8">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="bg-red-50 border border-red-200 p-6 rounded-lg text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-red-100 rounded-full">
              <HelpCircle className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-medium text-red-800 mb-2">Error Loading Portfolio</h3>
            <p className="text-red-700 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-red-600 text-white font-medium rounded-md hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  if (!userProfile) {
    return (
      <div className="min-h-screen bg-gray-100 py-8">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="bg-wayja-teal/10 border border-wayja-teal/30 p-6 rounded-lg text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-wayja-teal/20 rounded-full">
              <CircleDollarSign className="w-8 h-8 text-wayja-teal" />
            </div>
            <h3 className="text-lg font-medium text-wayja-dark-teal mb-2">No Portfolio Found</h3>
            <p className="text-gray-600 mb-4">You haven't set up your portfolio yet.</p>
            <Link 
              to="/wallet"
              className="inline-block px-4 py-2 bg-wayja-teal text-white font-medium rounded-md hover:bg-opacity-90"
            >
              Set Up Wallet
            </Link>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <header className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Portfolio</h1>
          <p className="text-sm text-gray-600 mt-1">Summary on Overview; full detail on Positions and Transactions.</p>
        </header>

        {/* Tab navigation */}
        <div className="mb-6 border-b border-gray-200 overflow-x-auto">
          <nav className="flex -mb-px min-w-full">
            <button
              onClick={() => setPortfolioTab('overview')}
              className={`py-4 px-6 font-medium text-sm sm:text-base whitespace-nowrap border-b-2 transition-colors flex-1 ${
                activeTab === 'overview'
                  ? 'border-wayja-teal text-wayja-teal'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setPortfolioTab('positions')}
              className={`py-4 px-6 font-medium text-sm sm:text-base whitespace-nowrap border-b-2 transition-colors flex-1 ${
                activeTab === 'positions'
                  ? 'border-wayja-teal text-wayja-teal'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Positions
            </button>
            <button
              onClick={() => setPortfolioTab('transactions')}
              className={`py-4 px-6 font-medium text-sm sm:text-base whitespace-nowrap border-b-2 transition-colors flex-1 ${
                activeTab === 'transactions'
                  ? 'border-wayja-teal text-wayja-teal'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Transactions
            </button>
            <button
              onClick={() => setPortfolioTab('created')}
              className={`py-4 px-6 font-medium text-sm sm:text-base whitespace-nowrap border-b-2 transition-colors flex-1 ${
                activeTab === 'created'
                  ? 'border-wayja-teal text-wayja-teal'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              My Markets
            </button>
          </nav>
        </div>
        
        {/* Content sections */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Portfolio summary */}
            <PortfolioSummary
              portfolioValue={portfolioValue}
              winRate={winRate}
              openPositions={openPositions}
              positions={positions}
              lmsrMarkets={lmsrMarkets}
              settledCount={settledTrades}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <button
                type="button"
                onClick={() => setPortfolioTab('positions')}
                className="text-left bg-white rounded-lg border border-gray-200 shadow-sm p-5 hover:border-wayja-teal/50 hover:shadow-md transition-all group"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="rounded-lg bg-wayja-teal/10 p-2">
                    <LayoutList className="w-5 h-5 text-wayja-teal" />
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-wayja-teal shrink-0" />
                </div>
                <p className="text-sm font-medium text-gray-500 mt-3">Positions</p>
                <p className="text-xl font-semibold text-gray-900">{openPositions} open</p>
                <p className="text-sm text-wayja-teal mt-1">Full list &amp; details</p>
              </button>
              <button
                type="button"
                onClick={() => setPortfolioTab('transactions')}
                className="text-left bg-white rounded-lg border border-gray-200 shadow-sm p-5 hover:border-wayja-teal/50 hover:shadow-md transition-all group"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="rounded-lg bg-gray-100 p-2">
                    <Receipt className="w-5 h-5 text-gray-700" />
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-wayja-teal shrink-0" />
                </div>
                <p className="text-sm font-medium text-gray-500 mt-3">Wallet activity</p>
                <p className="text-xl font-semibold text-gray-900">{transactions.length} entries</p>
                <p className="text-sm text-wayja-teal mt-1">Deposits, trades, payouts</p>
              </button>
              {createdMarkets.length > 0 ? (
                <button
                  type="button"
                  onClick={() => setPortfolioTab('created')}
                  className="text-left bg-white rounded-lg border border-gray-200 shadow-sm p-5 hover:border-wayja-teal/50 hover:shadow-md transition-all group"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="rounded-lg bg-amber-50 p-2">
                      <CircleDollarSign className="w-5 h-5 text-amber-700" />
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-wayja-teal shrink-0" />
                  </div>
                  <p className="text-sm font-medium text-gray-500 mt-3">Markets you created</p>
                  <p className="text-xl font-semibold text-gray-900">{createdMarkets.length}</p>
                  <p className="text-sm text-wayja-teal mt-1">Manage &amp; view</p>
                </button>
              ) : null}
            </div>
          </div>
        )}
        
        {activeTab === 'positions' && (
          <div className="space-y-6">
            <PortfolioPositions positions={positions} transactions={transactions} />
            <BetStatusExplainer />
          </div>
        )}
        
        {activeTab === 'transactions' && (
          <TransactionHistory transactions={transactions} />
        )}

        {activeTab === 'created' && (
           <div className="mt-6">
             <h2 className="text-xl font-semibold mb-4">Your Created Markets</h2>
             {createdMarkets.length === 0 ? (
               <div className="text-center text-gray-500 py-8">
                 <p>You haven't created any markets yet.</p>
                 <Link to="/account/create-market-form" className="mt-2 inline-block text-wayja-teal hover:underline">
                   Create your first market!
                 </Link>
               </div>
             ) : (
               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                 {createdMarkets.map(market => (
                   <MarketCard 
                     key={market.id} 
                     market={market} 
                     isAdmin={false} 
                     // Pass a dummy onWatchlistChange or implement if needed here
                     onWatchlistChange={() => {}} 
                   />
                 ))}
               </div>
             )}
           </div>
         )}
      </div>
    </div>
  );
} 